/**
 * lib/images.tsx
 * ------------------------------------------------------------------
 * Live site imagery. Starts from the IMG defaults and overlays the Firestore
 * `settings/images` document (publicly readable) so the admin Images page can
 * swap any picture by pasting a URL — no code change, no redeploy.
 *
 * A blank/whitespace override falls back to the default, so clearing a field
 * in the editor reverts that image. Falls back silently to defaults when
 * offline or before the doc exists. Mirrors lib/business.tsx.
 * ------------------------------------------------------------------
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { IMG, type ImageKey } from '@/data/images';

export type ImageMap = Record<ImageKey, string>;

const DEFAULTS = { ...IMG } as ImageMap;

function mergeOverrides(data: Record<string, unknown>): ImageMap {
  const next: ImageMap = { ...DEFAULTS };
  for (const key of Object.keys(DEFAULTS) as ImageKey[]) {
    const v = data[key];
    if (typeof v === 'string' && v.trim() !== '') next[key] = v.trim(); // blank → keep default
  }
  return next;
}

const ImagesContext = createContext<ImageMap>(DEFAULTS);

export function ImagesProvider({ children }: { children: ReactNode }) {
  const [images, setImages] = useState<ImageMap>(DEFAULTS);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'settings', 'images'),
      (snap) => setImages(snap.exists() ? mergeOverrides(snap.data() as Record<string, unknown>) : { ...DEFAULTS }),
      () => setImages({ ...DEFAULTS }), // offline / no doc — keep defaults
    );
  }, []);

  return <ImagesContext.Provider value={images}>{children}</ImagesContext.Provider>;
}

/** Live, settings-aware image map. Use this instead of importing IMG directly. */
export function useImages(): ImageMap {
  return useContext(ImagesContext);
}
